import { useEffect, useMemo, useState } from "react";
import { Box, Icon, IconButton, Link, Typography } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import { clampOverscroll, railArrowSx } from "src/utils/emblaUtils.js";
import { CONTENT_GUTTER_BLEED } from "src/styles/layout.js";
import { useDimensions } from "src/utils/useDimensions.js";

const RAIL_OPTIONS = {
  align: "start",
  dragFree: true,
  containScroll: "trimSnaps",
  slidesToScroll: 3,
};

/**
 * Bleeds the row past the shell's right gutter, matching the calendar rails on
 * the homepage.
 */
const RAIL_SCROLLER_SX = { position: "relative", mr: CONTENT_GUTTER_BLEED };

/** Short fallback shown in the circle until (or if) the logo image loads. */
const initials = (name) => name.replace(/[^A-Za-z0-9]/g, "").slice(0, 3).toUpperCase();

/**
 * One circular brand logo with the name centered below. Renders a Material
 * Symbols glyph when `symbol` is given; otherwise falls back to initials when
 * there is no logo or the image fails to load.
 *
 * @param {{ item: { name: string, logoUrl?: string, symbol?: string, to: string } }} props
 */
const LogoCircle = ({ item }) => {
  const [failed, setFailed] = useState(false);
  const showImage = item.logoUrl && !failed;

  return (
    <Box
      component={RouterLink}
      to={item.to}
      aria-label={item.name}
      sx={{ flex: "0 0 auto", width: 132, textAlign: "center", textDecoration: "none", color: "inherit" }}
    >
      <Box
        sx={{
          width: 120,
          height: 120,
          mx: "auto",
          borderRadius: "50%",
          overflow: "hidden",
          backgroundColor: "#fff",
          border: (theme) => `1px solid ${theme.palette.border.tertiary}`,
          boxShadow: "0 4px 14px rgba(0,0,0,0.10)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "transform 0.15s ease",
          "&:hover": { transform: "translateY(-3px)" },
        }}
      >
        {showImage ? (
          <Box
            component="img"
            src={item.logoUrl}
            alt={item.name}
            loading="lazy"
            onError={() => setFailed(true)}
            sx={{ width: "62%", height: "52%", objectFit: "contain" }}
          />
        ) : item.symbol ? (
          <Icon baseClassName="material-symbols-outlined" sx={{ fontSize: "3.25rem", color: "text.primary" }}>
            {item.symbol}
          </Icon>
        ) : (
          <Typography sx={{ fontWeight: 800, fontSize: "1.5rem", letterSpacing: "0.02em", color: "text.primary" }}>
            {initials(item.name)}
          </Typography>
        )}
      </Box>
      <Typography
        sx={{
          mt: 1.25,
          fontSize: "13px",
          fontWeight: 600,
          lineHeight: 1.25,
          minHeight: "2.5em",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {item.name}
      </Typography>
    </Box>
  );
};

/**
 * A rail of circular brand logos (leagues or streaming platforms). Finite
 * curated set, so it uses a plain scroll row rather than the paginated section
 * rail.
 *
 * @param {{ title: string, items: Array<{ name: string, logoUrl?: string,
 *   to: string }>, seeAllTo?: string }} props
 */
const LogoCircleRail = ({ title, items, seeAllTo, sx }) => {
  const { isMediumScreen } = useDimensions();
  const plugins = useMemo(() => [WheelGesturesPlugin({ forceWheelAxis: "x" })], []);
  const [emblaRef, emblaApi] = useEmblaCarousel(RAIL_OPTIONS, plugins);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  useEffect(() => {
    if (!emblaApi) return undefined;

    const update = () => {
      setCanScrollPrev(emblaApi.canScrollPrev());
      setCanScrollNext(emblaApi.canScrollNext());
    };
    const onScroll = () => {
      clampOverscroll(emblaApi);
    };

    emblaApi.on("scroll", onScroll);
    emblaApi.on("select", update);
    emblaApi.on("settle", update);
    emblaApi.on("reInit", update);
    emblaApi.on("resize", update);
    update();

    return () => {
      emblaApi.off("scroll", onScroll);
      emblaApi.off("select", update);
      emblaApi.off("settle", update);
      emblaApi.off("reInit", update);
      emblaApi.off("resize", update);
    };
  }, [emblaApi]);

  if (!items?.length) return null;

  return (
    <Box component="section" data-testid="logo-circle-rail" sx={{ mb: 7, ...sx }}>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 2, mb: 2 }}>
        <Typography variant="sectionTitle" component="h2">
          {title}
        </Typography>
        {seeAllTo && (
          <Link
            component={RouterLink}
            to={seeAllTo}
            sx={{ ml: "auto", flexShrink: 0, fontSize: "13px", fontWeight: 600, whiteSpace: "nowrap" }}
          >
            See all →
          </Link>
        )}
      </Box>

      <Box sx={RAIL_SCROLLER_SX}>
        {/* Dragging the rail would otherwise highlight the logo labels. */}
        <Box ref={emblaRef} sx={{ overflow: "hidden", userSelect: "none" }}>
          {/* py gives the circles room so overflow clipping doesn't shave their
              tops (or the hover lift) when zoomed. */}
          <Box sx={{ display: "flex", gap: 2.5, py: 1.5 }}>
            {items.map((item) => (
              <LogoCircle key={item.key ?? item.name} item={item} />
            ))}
          </Box>
        </Box>

        {!isMediumScreen && canScrollPrev && (
          <IconButton
            onClick={() => emblaApi?.scrollPrev()}
            aria-label={`Scroll ${title} left`}
            sx={railArrowSx("left")}
          >
            <ChevronLeft />
          </IconButton>
        )}
        {!isMediumScreen && canScrollNext && (
          <IconButton
            onClick={() => emblaApi?.scrollNext()}
            aria-label={`Scroll ${title} right`}
            data-testid="logo-circle-rail-next"
            sx={railArrowSx("right")}
          >
            <ChevronRight />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

export default LogoCircleRail;
